import { useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import 'theme/global.scss';
import '@carbon/charts/styles.css';
import 'react-tooltip/dist/react-tooltip.css';
import { Loading } from '@carbon/react';

import { NotificationsProvider, useNotifications } from 'core/notifications';
import { PageErrorProvider } from 'core/pageError';
import AppQueryProvider from 'core/query-client/AppQueryProvider';
import { USER_ROLE } from 'core/user/role';
import useIsCallbackRoute from 'core/user/useIsCallbackRoute';
import { UserProvider, useUser } from 'core/user/user';
import AuthCallback from 'features/auth/AuthCallback';
import Layout from 'features/layout/Layout';
import Notifications from 'features/layout/Notifications';
import GroupsAdministrationPage from 'pages/admin/GroupsAdministrationPage';
import UsersAdministrationPage from 'pages/admin/UsersAdministrationPage';
import AllProjectsPage from 'pages/AllProjectsPage';
import ArchivedProjectsPage from 'pages/ArchivedProjectsPage';
import LoginPage from 'pages/auth/LoginPage';
import CreateProjectPage from 'pages/CreateProjectPage';
import DashboardComparisonPage from 'pages/dashboard/DashboardComparisonPage';
import DashboardPage from 'pages/dashboard/DashboardPage';
import ExportProjectPage from 'pages/dashboard/ExportProjectPage';
import InputsPage from 'pages/InputsPage';
import LandingPage from 'pages/LandingPage';
import OutputsPage from 'pages/OutputsPage';
import NotFoundPage from 'pages/shared/NotFoundPage';
import ProtectedPage from 'pages/shared/ProtectedPage';

const Routing = () => {
  const isCallback = useIsCallbackRoute();
  const { isLoading, hasError, hasAccess, hasSessionExpired, user } = useUser();
  const { showErrorNotification } = useNotifications();

  const shouldLogin = hasError || (!user && !hasSessionExpired);

  useEffect(() => {
    if (hasError) {
      showErrorNotification({
        description: 'Failed to authenticate. Please try again later'
      });
    }
  }, [hasError]);

  return (
    <Layout noPadding={shouldLogin}>
      <Loading withOverlay active={isLoading} />
      {!isLoading && (
        <Routes>
          <Route
            index
            element={
              shouldLogin ? <LoginPage hasAccess={hasAccess !== false} /> : <ProtectedPage component={LandingPage} />
            }
          />
          {!hasError && (
            <>
              <Route path="projects">
                <Route index element={<ProtectedPage component={AllProjectsPage} />} />
                <Route path="archive" element={<ProtectedPage component={ArchivedProjectsPage} />} />
                <Route
                  path="inputs"
                  element={<ProtectedPage component={CreateProjectPage} role={USER_ROLE.initiator} />}
                />
                <Route path=":projectId">
                  <Route path="inputs" element={<ProtectedPage component={InputsPage} />} />
                  <Route path="outputs" element={<ProtectedPage component={OutputsPage} />} />
                  <Route path="dashboard" element={<ProtectedPage component={DashboardPage} />} />
                  <Route path="export" element={<ProtectedPage component={ExportProjectPage} />} />
                  <Route
                    path="compare/:otherProjectId"
                    element={<ProtectedPage component={DashboardComparisonPage} />}
                  />
                </Route>
              </Route>
              <Route path="administration">
                <Route
                  path="users"
                  element={<ProtectedPage component={UsersAdministrationPage} role={USER_ROLE.admin} />}
                />
                <Route
                  path="groups"
                  element={<ProtectedPage component={GroupsAdministrationPage} role={USER_ROLE.admin} />}
                />
              </Route>
            </>
          )}
          {isCallback && <Route path="authentication/callback" element={<AuthCallback />} />}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      )}
    </Layout>
  );
};

const App = () => (
  <AppQueryProvider>
    <BrowserRouter>
      <PageErrorProvider>
        <NotificationsProvider>
          <Notifications />
          <UserProvider>
            <Routing />
          </UserProvider>
        </NotificationsProvider>
      </PageErrorProvider>
    </BrowserRouter>
  </AppQueryProvider>
);

export default App;
